import {
  AppointmentConfirmedTickIcon,
  CalendarIcon,
  ClockIcon,
} from '@/components/atoms/icons';
import { Typography } from '@/components/atoms/Typography';
import { ContentDialog } from '@/components/molecules/dialogs/ContentDialog';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useSalesAgentContact, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { isSmallScreen } from '@/core/utils/responsive';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { SHOW_SALES_AGENT_CONTACT } from '../constants/salesAgentVisibility';
import { useAppointmentLabels } from '../hooks/useAppointmentLabels';
import type { BookedAppointment } from '../types/appointment.types';
import { AppointmentSalesContact } from './AppointmentSalesContact';

export interface AppointmentConfirmedDialogProps {
  visible: boolean;
  appointment: BookedAppointment | null;
  onOk: () => void;
}

/**
 * Success dialog after booking / rescheduling — content from `appointment` props.
 * Web book-appointment flow only (not the mobile molecules dialog).
 */
export function AppointmentConfirmedDialog({
  visible,
  appointment,
  onOk,
}: Readonly<AppointmentConfirmedDialogProps>) {
  React.useEffect(() => {
    if (visible) {
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.APPOINTMENT,
        actionName: ALMETLAA_ACTION.APPOINTMENT.CONFIRMED_DIALOG_VIEW,
        eventType: 'view',
      });
    }
  }, [visible]);
  const colors = useUIColors();
  const labels = useAppointmentLabels();
  const isMobile = isSmallScreen();
  const salesAgent = useSalesAgentContact();
  const dialColor = colors.appointmentBadgeText;
  /** Mobile: Font/Size/3XS (caption 12 / Regular / LH 100%); desktop: body / LH 22 */
  const metaVariant = isMobile ? 'caption' : 'body';
  const metaStyle = isMobile ? styles.metaTextMobile : styles.metaText;
  const valueStyle = isMobile ? styles.valueTextMobile : styles.valueText;
  /** Mobile: Font/Size/Base SemiBold / LH 100% → headline; desktop: title2 */
  const titleVariant = isMobile ? 'headline' : 'title2';

  const hasSalesAgent =
    salesAgent.name.length > 0 || salesAgent.phone.length > 0;
  const salesContact =
    SHOW_SALES_AGENT_CONTACT && hasSalesAgent ? (
      <AppointmentSalesContact
        label={salesAgent.name}
        phone={salesAgent.phone}
        color={dialColor}
        size="detail"
      />
    ) : null;

  return (
    <ContentDialog
      visible={visible}
      onClose={onOk}
      contentAlign="center"
      maxWidth={420}
      // Allow phone tooltip to paint below the dial icon (card clips by default).
      cardStyle={styles.cardOverflow}
      icon={<AppointmentConfirmedTickIcon size={56} />}
      title={labels.CONFIRMED_TITLE}
      titleVariant={titleVariant}
      titleColor="primary"
      actions={[
        {
          label: labels.OK,
          variant: 'primary',
          onPress: onOk,
          flex: false,
        },
      ]}
    >
      {appointment ? (
        <View
          style={[
            styles.summary,
            { backgroundColor: colors.backgroundLevel2 },
          ]}
        >
          <View style={styles.row}>
            <View style={[styles.field, styles.dateField]}>
              <Typography
                variant={metaVariant}
                color="secondary"
                style={metaStyle}
              >
                {labels.CONFIRMED_DATE}
              </Typography>
              <View style={styles.valueRow}>
                <CalendarIcon size={16} color={colors.fontSecondary} />
                <Typography
                  variant={metaVariant}
                  color="secondary"
                  style={valueStyle}
                  numberOfLines={1}
                >
                  {appointment.dateDisplay}
                </Typography>
              </View>
            </View>
            <View style={[styles.field, styles.timeField]}>
              <Typography
                variant={metaVariant}
                color="secondary"
                style={metaStyle}
              >
                {labels.CONFIRMED_TIME}
              </Typography>
              <View style={styles.valueRow}>
                <ClockIcon size={16} color={colors.fontSecondary} />
                <Typography
                  variant={metaVariant}
                  color="secondary"
                  style={valueStyle}
                  numberOfLines={1}
                >
                  {appointment.timeLabel}
                </Typography>
              </View>
            </View>
          </View>
          {salesContact ? (
            <View style={styles.agentRow}>
              <Typography
                variant={metaVariant}
                color="secondary"
                style={metaStyle}
              >
                {labels.CONFIRMED_AGENT}
              </Typography>
              {salesContact}
            </View>
          ) : null}
        </View>
      ) : null}
    </ContentDialog>
  );
}

const styles = StyleSheet.create({
  cardOverflow: {
    overflow: 'visible',
  },
  summary: {
    borderRadius: 12,
    padding: 16,
    gap: 20,
    overflow: 'visible',
  },
  row: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  agentRow: {
    gap: 6,
    minWidth: 0,
    overflow: 'visible',
  },
  field: {
    gap: 6,
  },
  /** More room for long dates like "8 August 2026" */
  dateField: {
    flex: 1.35,
    minWidth: 0,
  },
  /** Time is shorter — shift right / take less space */
  timeField: {
    flex: 0.85,
    minWidth: 0,
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  /** Desktop: base 16 / Regular / LH 22 */
  metaText: {
    lineHeight: 22,
  },
  valueText: {
    flexShrink: 1,
    lineHeight: 22,
    transform: [{ translateY: 1.1 }],
  },
  /** Mobile: Font/Size/3XS → caption 12 / Regular / LH 100% */
  metaTextMobile: {
    lineHeight: 12,
  },
  valueTextMobile: {
    flexShrink: 1,
    lineHeight: 12,
  },
});

export default AppointmentConfirmedDialog;
